// src/ui.js
// ----------------------------------------------------------------------------
// Interactivity that doesn't belong to the reveal engine. Everything here is
// bound to the DOM nodes that render.js just injected.
//
//   - Typed.js hero subtitle (typewriter)
//   - Theme toggle (dark/light, persisted + respects os preference)
//   - Mobile nav toggle (open/close + icon swap)
//   - Scroll-spy nav highlighting (IntersectionObserver)
//   - Back-to-top button (CSS `.show` on scroll)
//   - Custom canvas node-link background for the hero (rAF, paused when hidden)
//   - Contact form → success toast driven by Motion UI's JS API (animateIn/Out)
// ----------------------------------------------------------------------------
import Typed from 'typed.js';
import { icon } from './icons.js';
import 'lazysizes';

const THEME_KEY = 'theme';

function root() {
  return document.documentElement;
}
function getPreferredTheme() {
  const stored = localStorage.getItem(THEME_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}
function applyTheme(theme) {
  root().setAttribute('data-theme', theme);
}

// ---------------------------------------------------------------------------
// Typed.js — hero subtitle
// ---------------------------------------------------------------------------
function initTyped(profile) {
  const el = document.getElementById('heroTitle');
  if (!el) return;
  const strings = [profile.title || 'Full-stack engineer', 'Real-time systems & motion-rich UI.', 'Shipping type-safe, accessible web apps.'];
  // eslint-disable-next-line no-undef
  const TypedCtor = window.Typed || Typed;
  if (!TypedCtor) return;
  new TypedCtor(el, {
    strings,
    typeSpeed: 55,
    backSpeed: 35,
    backDelay: 1600,
    startOnScroll: false,
    loop: false,
    showCursor: true,
    cursorChar: '|',
    onBegin: () => { el.classList.add('typed'); },
  });
}

// ---------------------------------------------------------------------------
// Theme toggle
// ---------------------------------------------------------------------------
function initTheme() {
  const toggle = document.getElementById('themeToggle');
  if (!toggle) return;
  const current = getPreferredTheme();
  applyTheme(current);
  toggle.innerHTML = icon(current === 'dark' ? 'moon' : 'sun');
  toggle.setAttribute('aria-pressed', String(current === 'light'));

  toggle.addEventListener('click', () => {
    const next = root().getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
    applyTheme(next);
    localStorage.setItem(THEME_KEY, next);
    toggle.innerHTML = icon(next === 'dark' ? 'moon' : 'sun');
    toggle.setAttribute('aria-pressed', String(next === 'light'));
    // Redraw the canvas so its accent reads against the new theme.
    if (window.__canvasTick) window.__canvasTick();
  });
}

// ---------------------------------------------------------------------------
// Mobile nav
// ---------------------------------------------------------------------------
function initNav() {
  const toggle = document.getElementById('navToggle');
  const list = document.getElementById('navList');
  if (!toggle || !list) return;
  const updateIcon = () => (toggle.innerHTML = list.classList.contains('open') ? icon('x') : icon('menu'));
  toggle.addEventListener('click', () => {
    const open = !list.classList.contains('open');
    list.classList.toggle('open', open);
    toggle.setAttribute('aria-expanded', String(open));
    updateIcon();
  });
  // Close the menu when a link is chosen.
  list.addEventListener('click', (e) => {
    const a = e.target.closest('a');
    if (a) {
      list.classList.remove('open');
      toggle.setAttribute('aria-expanded', 'false');
      updateIcon();
    }
  });
}

// ---------------------------------------------------------------------------
// Scroll-spy
// ---------------------------------------------------------------------------
function initScrollSpy() {
  const ids = ['hero', 'about', 'experience', 'work', 'projects', 'academic', 'awards', 'contact'];
  const links = {};
  ids.forEach((id) => {
    const a = document.querySelector(`nav a[href="#${id}"]`);
    if (a) links[id] = a;
  });
  const sections = ids.map((id) => document.getElementById(id)).filter(Boolean);
  if (!sections.length) return;
  const onIntersect = (entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) {
        const id = e.target.id;
        Object.values(links).forEach((a) => a.classList.remove('nav__link--active'));
        if (links[id]) links[id].classList.add('nav__link--active');
      }
    });
  };
  const io = new IntersectionObserver(onIntersect, { threshold: 0.45, rootMargin: '-20% 0px -55% 0px' });
  sections.forEach((s) => io.observe(s));
}

// ---------------------------------------------------------------------------
// Back-to-top
// ---------------------------------------------------------------------------
function initBackToTop() {
  const btn = document.getElementById('topBtn');
  if (!btn) return;
  let showing = false;
  const onScroll = () => {
    const should = window.scrollY > 480;
    if (should === showing) return;
    showing = should;
    btn.classList.toggle('show', should);
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

// ---------------------------------------------------------------------------
// Contact form → Mailto submission
// ---------------------------------------------------------------------------
function ensureToast() {
  let toast = document.getElementById('formToast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'formToast';
    toast.className = 'toast';
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'polite');
    document.body.appendChild(toast);
  }
  return toast;
}

function initContactForm(profileEmail) {
  const form = document.getElementById('contactForm');
  if (!form) return;
  const btn = form.querySelector('button[type="submit"]');
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    if (btn) btn.disabled = true;
    
    const name = form.querySelector('#cName').value;
    const email = form.querySelector('#cEmail').value;
    const message = form.querySelector('#cMessage').value;
    
    const subject = encodeURIComponent(`Portfolio Contact from ${name}`);
    const body = encodeURIComponent(`Name: ${name}\nEmail: ${email}\n\nMessage:\n${message}`);
    
    window.location.href = `mailto:${profileEmail}?subject=${subject}&body=${body}`;
    
    setTimeout(() => {
      if (btn) btn.disabled = false;
      form.reset();
    }, 1000);
  });
}

/** Mount all non-reveal UI behaviour. Called from main.js after render. */
export function bootUI(data, MotionUI) {
  initTheme();
  initNav();
  initScrollSpy();
  initBackToTop();
  initTyped(data.profile);
  initContactForm(data.profile.email);
  
  // Set favicon dynamically
  const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
  link.type = 'image/x-icon';
  link.rel = 'icon';
  link.href = data.profile.photo;
  document.getElementsByTagName('head')[0].appendChild(link);
}
